import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import NavbarV2 from "./NavbarV2";
import FooterV2 from "./FooterV2";
import { useReveal, useCountUp } from "./useReveal";
import { DEFAULT_LANG } from "../../i18n";

import "./tokens.css";
import "./HomeV2.css";

/* product imagery (reused from existing assets) */
import pHealiary from "../../assets/images/03rpm/service3.svg";
import pAtti from "../../assets/images/03rpm/service2.svg";
import pOsto from "../../assets/images/03rpm/service1.svg";
import pDrug from "../../assets/images/03rpm/drug_card.svg";

/* trust logos (hospitals & research institutions) */
import logo1 from "../../assets/images/02about_partners/01suh.png";
import logo2 from "../../assets/images/02about_partners/02gsh.png";
import logo3 from "../../assets/images/02about_partners/03auh.png";
import logo4 from "../../assets/images/02about_partners/04eumc.png";
import logo5 from "../../assets/images/02about_partners/05cuh.png";
import logo6 from "../../assets/images/02about_partners/07ssh.png";
import logo7 from "../../assets/images/02about_partners/08kcsg.png";
import logo8 from "../../assets/images/02about_partners/KGOG.png";

const getLangFromPath = (pathname) => {
  const m = pathname.match(/^\/(kr|en)(\/|$)/);
  return m ? m[1] : DEFAULT_LANG;
};

/* ---- inline icons ------------------------------------------------------- */
const Icon = ({ d, fill }) => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor"
    strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} fill={fill || "none"} />}
  </svg>
);
const ICONS = {
  patient: ["M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z", "M4 20a8 8 0 0 1 16 0"],
  data: ["M3 12h3l2 6 4-14 2 8h7"],
  platform: ["M12 3 3 8l9 5 9-5-9-5Z", "M3 13l9 5 9-5", "M3 17.5l9 5 9-5"],
  dashboard: ["M4 4h16v16H4z", "M8 14v3", "M12 10v7", "M16 7v10"],
  provider: ["M6 3v5a4 4 0 0 0 8 0V3", "M10 16v1a4 4 0 0 0 8 0v-2", "M18 13a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"],
  research: ["M9 3h6", "M10 3v6l-4 8a2 2 0 0 0 2 3h8a2 2 0 0 0 2-3l-4-8V3", "M7.5 15h9"],
  digital: ["M7 4h10a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z", "M11 18h2"],
  monitor: ["M3 12h4l2 5 4-10 2 5h6"],
  flask: ["M9 3h6", "M10 3v6l-4 8a2 2 0 0 0 2 3h8a2 2 0 0 0 2-3l-4-8V3"],
  arrow: ["M5 12h14", "M13 6l6 6-6 6"],
};

export default function HomeV2() {
  const { t } = useTranslation();
  const history = useHistory();
  const lang = getLangFromPath(useLocation().pathname);
  const go = (p) => history.push(`/${lang}${p}`);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  /* ---- data ---- */
  const flow = [
    { key: "patient", icon: "patient" },
    { key: "data", icon: "data" },
    { key: "platform", icon: "platform" },
    { key: "dashboard", icon: "dashboard" },
    { key: "provider", icon: "provider" },
    { key: "research", icon: "research" },
  ];

  const pillars = [
    { key: "digital", icon: "digital", to: "/healthcare/" },
    { key: "rpm", icon: "monitor", to: "/rpmsolution/" },
    { key: "research", icon: "flask", to: "/clinicalresearch/" },
  ];

  const products = [
    { key: "healiary", img: pHealiary, to: "/healthcare/" },
    { key: "atti", img: pAtti, to: "/rpmsolution/" },
    { key: "ostoCare", img: pOsto, to: "/rpmsolution/" },
    { key: "drugDiary", img: pDrug, to: "/healthcare/" },
  ];

  const pipeline = ["patient", "collect", "platform", "insight", "outcomes"];

  const stats = [
    { key: "publications", end: 140, suffix: "+" },
    { key: "trials", end: 120, suffix: "+" },
    { key: "partners", end: 10, suffix: "+" },
    { key: "products", end: 4, suffix: "" },
  ];

  const trustLogos = [logo1, logo2, logo3, logo4, logo5, logo6, logo7, logo8];
  /* duplicated for a seamless right-to-left marquee loop */
  const trustMarquee = [...trustLogos, ...trustLogos];

  /* ---- product showcase active tracking (sticky scroll) ---- */
  const [activeProduct, setActiveProduct] = useState(0);
  const productRefs = useRef([]);
  useEffect(() => {
    const reduce =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const idx = Number(e.target.getAttribute("data-idx"));
            setActiveProduct(idx);
          }
        });
      },
      { threshold: 0.5, rootMargin: "-25% 0px -25% 0px" }
    );
    productRefs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  /* reveal refs */
  const whatRef = useReveal();
  const platformRef = useReveal();
  const statsWrapRef = useReveal();
  const newsRef = useReveal();
  const ctaRef = useReveal();

  return (
    <div className="v2-root">
      <NavbarV2 />

      {/* ================= HERO ================= */}
      <section className="v2-hero">
        <div className="v2-hero-bg" aria-hidden="true" />
        <div className="v2-hero-inner v2-wrap">
          <div className="v2-hero-copy">
            <span className="v2-hero-badge">{t("homeV2.hero.badge")}</span>
            <h1 className="v2-hero-title">
              {t("homeV2.hero.title1")}{" "}
              <span className="v2-hl">{t("homeV2.hero.titleHl")}</span>{" "}
              {t("homeV2.hero.title2")}
            </h1>
            <p className="v2-hero-sub">{t("homeV2.hero.sub")}</p>
            <div className="v2-hero-actions">
              <button className="v2-btn v2-btn-primary" onClick={() => go("/rpmsolution/")}>
                {t("homeV2.hero.ctaPrimary")}
                <Icon d={ICONS.arrow} />
              </button>
              <button
                className="v2-btn v2-btn-ghost"
                onClick={() => {
                  const el = document.querySelector("#v2-products");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
              >
                {t("homeV2.hero.ctaSecondary")}
              </button>
            </div>
          </div>

          {/* ---- animated ecosystem diagram ---- */}
          <div className="v2-eco" aria-label="Herings ecosystem flow">
            <div className="v2-eco-panel">
              {flow.map((n, i) => (
                <React.Fragment key={n.key}>
                  <div className="v2-eco-node" style={{ "--i": i }}>
                    <span className="v2-eco-ico">
                      <Icon d={ICONS[n.icon]} />
                    </span>
                    <div className="v2-eco-text">
                      <span className="v2-eco-label">{t(`homeV2.flow.${n.key}.label`)}</span>
                      <span className="v2-eco-sub">{t(`homeV2.flow.${n.key}.sub`)}</span>
                    </div>
                  </div>
                  {i < flow.length - 1 && (
                    <div className="v2-eco-connector" style={{ "--i": i }}>
                      <span className="v2-eco-pulse" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= TRUST STRIP ================= */}
      <section className="v2-trust">
        <div className="v2-wrap">
          <p className="v2-trust-label">{t("homeV2.trust.label")}</p>
        </div>
        <div className="v2-trust-marquee">
          <div className="v2-trust-track" aria-hidden="true">
            {trustMarquee.map((src, i) => (
              <div className="v2-trust-logo" key={i}>
                <img src={src} alt="" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= WHAT WE DO ================= */}
      <section className="v2-section" ref={whatRef}>
        <div className="v2-wrap">
          <div className="v2-sec-head v2-reveal is-visible">
            <span className="v2-eyebrow">{t("homeV2.what.eyebrow")}</span>
            <h2 className="v2-h2">{t("homeV2.what.title")}</h2>
            <p className="v2-lead v2-sec-lead">{t("homeV2.what.lead")}</p>
          </div>
          <div className="v2-pillars">
            {pillars.map((p, i) => (
              <button
                key={p.key}
                className="v2-pillar v2-reveal is-visible"
                style={{ transitionDelay: `${i * 80}ms` }}
                onClick={() => go(p.to)}
              >
                <span className="v2-pillar-ico">
                  <Icon d={ICONS[p.icon]} />
                </span>
                <h3 className="v2-pillar-title">{t(`homeV2.what.pillars.${p.key}.title`)}</h3>
                <p className="v2-pillar-desc">{t(`homeV2.what.pillars.${p.key}.desc`)}</p>
                <span className="v2-pillar-link">
                  {t("homeV2.common.learnMore")} <Icon d={ICONS.arrow} />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PRODUCT SHOWCASE ================= */}
      <section className="v2-show" id="v2-products">
        <div className="v2-wrap v2-show-grid">
          <div className="v2-show-sticky">
            <span className="v2-eyebrow">{t("homeV2.products.eyebrow")}</span>
            <h2 className="v2-h2">{t("homeV2.products.title")}</h2>
            <p className="v2-lead">{t("homeV2.products.lead")}</p>
            <div className="v2-show-visual">
              {products.map((p, i) => (
                <div
                  key={p.key}
                  className={`v2-show-img ${activeProduct === i ? "is-active" : ""}`}
                >
                  <img src={p.img} alt={t(`homeV2.products.items.${p.key}.name`)} />
                </div>
              ))}
              <div className="v2-show-dots">
                {products.map((_, i) => (
                  <span key={i} className={activeProduct === i ? "is-active" : ""} />
                ))}
              </div>
            </div>
          </div>

          <div className="v2-show-list">
            {products.map((p, i) => (
              <article
                key={p.key}
                data-idx={i}
                ref={(el) => (productRefs.current[i] = el)}
                className={`v2-show-item ${activeProduct === i ? "is-active" : ""}`}
              >
                <div className="v2-show-item-media">
                  <img src={p.img} alt={t(`homeV2.products.items.${p.key}.name`)} loading="lazy" />
                </div>
                <span className="v2-show-tag">{t(`homeV2.products.items.${p.key}.tag`)}</span>
                <h3 className="v2-show-name">{t(`homeV2.products.items.${p.key}.name`)}</h3>
                <p className="v2-show-desc">{t(`homeV2.products.items.${p.key}.desc`)}</p>
                <div className="v2-show-data">
                  <span className="v2-show-data-dot" />
                  {t(`homeV2.products.items.${p.key}.data`)}
                </div>
                <button className="v2-show-link" onClick={() => go(p.to)}>
                  {t("homeV2.common.learnMore")} <Icon d={ICONS.arrow} />
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PLATFORM / DATA FLOW ================= */}
      <section className="v2-platform" ref={platformRef}>
        <div className="v2-wrap">
          <div className="v2-sec-head v2-reveal">
            <span className="v2-eyebrow v2-eyebrow-teal">{t("homeV2.platform.eyebrow")}</span>
            <h2 className="v2-h2 v2-h2-light">{t("homeV2.platform.title")}</h2>
            <p className="v2-lead v2-sec-lead v2-lead-light">{t("homeV2.platform.lead")}</p>
          </div>
          <div className="v2-pipe v2-reveal">
            {pipeline.map((step, i) => (
              <React.Fragment key={step}>
                <div className="v2-pipe-step" style={{ transitionDelay: `${i * 90}ms` }}>
                  <span className="v2-pipe-num">{i + 1}</span>
                  <span className="v2-pipe-label">{t(`homeV2.platform.steps.${step}.label`)}</span>
                  <span className="v2-pipe-sub">{t(`homeV2.platform.steps.${step}.sub`)}</span>
                </div>
                {i < pipeline.length - 1 && <span className="v2-pipe-arrow" aria-hidden="true" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ================= BY THE NUMBERS ================= */}
      <section className="v2-section v2-stats-sec" ref={statsWrapRef}>
        <div className="v2-wrap">
          <div className="v2-stats">
            {stats.map((s) => (
              <Stat key={s.key} end={s.end} suffix={s.suffix} label={t(`homeV2.stats.${s.key}`)} />
            ))}
          </div>
        </div>
      </section>

      {/* ================= NEWS ================= */}
      <section className="v2-section" ref={newsRef}>
        <div className="v2-wrap">
          <div className="v2-news-head v2-reveal">
            <div>
              <span className="v2-eyebrow">{t("homeV2.news.eyebrow")}</span>
              <h2 className="v2-h2">{t("homeV2.news.title")}</h2>
            </div>
            <button className="v2-btn v2-btn-ghost" onClick={() => go("/news/")}>
              {t("homeV2.news.viewAll")}
              <Icon d={ICONS.arrow} />
            </button>
          </div>
          <div className="v2-news-grid">
            {[0, 1, 2].map((i) => (
              <button
                key={i}
                className="v2-news-card v2-reveal"
                style={{ transitionDelay: `${i * 80}ms` }}
                onClick={() => go("/news/")}
              >
                <span className="v2-news-tag">{t(`homeV2.news.items.${i}.tag`)}</span>
                <h3 className="v2-news-title">{t(`homeV2.news.items.${i}.title`)}</h3>
                <span className="v2-news-link">
                  {t("homeV2.common.readMore")} <Icon d={ICONS.arrow} />
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="v2-cta-sec" ref={ctaRef}>
        <div className="v2-wrap v2-cta v2-reveal">
          <div className="v2-cta-glow" aria-hidden="true" />
          <h2 className="v2-cta-title">{t("homeV2.cta.title")}</h2>
          <p className="v2-cta-sub">{t("homeV2.cta.sub")}</p>
          <div className="v2-cta-actions">
            <button className="v2-btn v2-btn-primary" onClick={() => go("/contactus/")}>
              {t("homeV2.cta.primary")}
              <Icon d={ICONS.arrow} />
            </button>
            <button className="v2-btn v2-btn-ghost v2-btn-ghost-dark" onClick={() => go("/whoweare/")}>
              {t("homeV2.cta.secondary")}
            </button>
          </div>
        </div>
      </section>

      <FooterV2 />
    </div>
  );
}

function Stat({ end, suffix, label }) {
  const [ref, value] = useCountUp(end);
  return (
    <div className="v2-stat" ref={ref}>
      <span className="v2-stat-num">
        {value}
        <span className="v2-stat-suffix">{suffix}</span>
      </span>
      <span className="v2-stat-label">{label}</span>
    </div>
  );
}
